import React, { useMemo, useState } from 'react';
import { Theme } from '../types';
import { DEFAULT_THEMES, TRANSLATIONS } from '../constants';
import { Palette, X } from 'lucide-react';

interface ThemeSelectorProps {
  selectedTheme: Theme;
  language: 'ko' | 'en';
  isDarkMode?: boolean;
  onThemeChange: (theme: Theme) => void;
  isMobileModalMode?: boolean;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = React.memo(({
  selectedTheme,
  language,
  isDarkMode = false,
  onThemeChange,
  isMobileModalMode = false,
}) => {
  const t = TRANSLATIONS[language];
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Memoize theme rows to avoid recalculation
  const themeRows = useMemo(() => {
    const themesPerRow = 5;
    const rows = [];
    for (let i = 0; i < DEFAULT_THEMES.length; i += themesPerRow) {
      rows.push(DEFAULT_THEMES.slice(i, i + themesPerRow));
    }
    return rows;
  }, []);
  
  const handleThemeChange = (theme: Theme) => {
    onThemeChange(theme);
    if (isMobileModalMode) {
      setIsModalOpen(false);
    }
  };
  
  // 모바일 모달 모드일 때
  if (isMobileModalMode) {
    const currentThemeIndex = DEFAULT_THEMES.findIndex(theme => 
      JSON.stringify(theme) === JSON.stringify(selectedTheme)
    );
    
    return (
      <>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full h-full flex items-center justify-center"
        >
          <div className="flex space-x-0.5">
            <div
              className="w-3 h-3 rounded-full border border-gray-300"
              style={{ backgroundColor: selectedTheme.primary }}
            />
            <div
              className="w-3 h-3 rounded-full border border-gray-300"
              style={{ backgroundColor: selectedTheme.secondary }}
            />
            <div
              className="w-3 h-3 rounded-full border border-gray-300"
              style={{ backgroundColor: selectedTheme.accent }}
            />
            <div
              className="w-3 h-3 rounded-full border border-gray-300"
              style={{ backgroundColor: selectedTheme.quaternary }}
            />
          </div>
        </button>
        
        {/* 모바일 모달 */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 pointer-events-none">
            {/* 상단 모달 */}
            <div 
              className={`absolute top-0 left-0 right-0 min-h-[18vh] max-h-[40vh] pointer-events-auto transform transition-transform duration-300 ${
                isModalOpen ? 'translate-y-0' : '-translate-y-full'
              } ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } border-b shadow-lg flex flex-col`}
            >
              {/* 닫기 버튼 */}
              <button
                onClick={() => setIsModalOpen(false)}
                className={`absolute top-2 right-2 p-1 rounded-lg transition-colors z-10 ${
                  isDarkMode
                    ? 'hover:bg-gray-700 text-gray-400'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <X className="w-4 h-4" />
              </button>
              
              {/* 모달 콘텐츠 */}
              <div className="flex-1 overflow-y-auto p-3">
                <h3 className={`text-sm font-medium mb-2 flex items-center gap-2 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  <Palette className="w-4 h-4" />
                  {t.themeColors || '테마 색상'}
                </h3>
                
                <div className="flex flex-wrap gap-2 justify-center">
                  {DEFAULT_THEMES.map((theme, themeIndex) => {
                    const isSelected = JSON.stringify(theme) === JSON.stringify(selectedTheme);
                    
                    return (
                      <button
                        key={themeIndex}
                        onClick={() => handleThemeChange(theme)}
                        className={`
                          relative flex items-center p-1.5 sm:p-2 rounded border-2 transition-all hover:scale-105
                          ${isSelected
                            ? isDarkMode 
                              ? 'border-blue-400 bg-blue-900/20' 
                              : 'border-blue-500 bg-blue-50'
                            : isDarkMode
                              ? 'border-gray-600 hover:border-gray-500 bg-gray-800'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }
                        `}
                        title={`Theme ${themeIndex + 1}`}
                      >
                        <div className="flex gap-0.5 sm:gap-1">
                          <div
                            className="w-3.5 h-3.5 sm:w-5 sm:h-5 rounded-full border border-gray-300"
                            style={{ backgroundColor: theme.primary }}
                          />
                          <div
                            className="w-3.5 h-3.5 sm:w-5 sm:h-5 rounded-full border border-gray-300"
                            style={{ backgroundColor: theme.secondary }}
                          />
                          <div
                            className="w-3.5 h-3.5 sm:w-5 sm:h-5 rounded-full border border-gray-300"
                            style={{ backgroundColor: theme.accent }}
                          />
                          <div
                            className="w-3.5 h-3.5 sm:w-5 sm:h-5 rounded-full border border-gray-300"
                            style={{ backgroundColor: theme.quaternary }}
                          />
                        </div>
                        {isSelected && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center">
                            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            
            {/* 배경 클릭 영역 - 메인 화면은 클릭 가능 */}
            <div 
              className="absolute inset-0 pointer-events-auto"
              onClick={() => setIsModalOpen(false)}
              style={{ top: '20%' }}
            />
          </div>
        )}
      </>
    );
  }
  
  // 데스크톱 모드 (기존 코드)

  return (
    <div className="space-y-1">
      <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        {t.themeColors}
      </label>
      <div className="space-y-0.5">
        {themeRows.map((row, rowIndex) => (
          <div key={rowIndex} className="flex gap-1">
            {row.map((theme, index) => (
              <button
                key={rowIndex * 5 + index}
                onClick={() => onThemeChange(theme)}
                className={`
                  flex space-x-0.5 p-1 rounded border transition-all hover:scale-105 flex-1
                  ${JSON.stringify(theme) === JSON.stringify(selectedTheme)
                    ? isDarkMode 
                      ? 'border-blue-400 ring-1 ring-blue-500 bg-gray-700' 
                      : 'border-gray-400 ring-1 ring-blue-500 bg-white'
                    : isDarkMode
                      ? 'border-gray-600 hover:border-gray-500 bg-gray-800'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }
                `}
                title={`Theme ${rowIndex * 5 + index + 1}`}
              >
                <div
                  className="w-1.5 h-1.5 rounded-full border border-gray-300"
                  style={{ backgroundColor: theme.primary }}
                />
                <div
                  className="w-1.5 h-1.5 rounded-full border border-gray-300"
                  style={{ backgroundColor: theme.secondary }}
                />
                <div
                  className="w-1.5 h-1.5 rounded-full border border-gray-300"
                  style={{ backgroundColor: theme.accent }}
                />
                <div
                  className="w-1.5 h-1.5 rounded-full border border-gray-300"
                  style={{ backgroundColor: theme.quaternary }}
                />
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    JSON.stringify(prevProps.selectedTheme) === JSON.stringify(nextProps.selectedTheme) &&
    prevProps.language === nextProps.language &&
    prevProps.isDarkMode === nextProps.isDarkMode
  );
});

ThemeSelector.displayName = 'ThemeSelector';